import React, { useState, useEffect, useRef } from 'react';
import {
    Button,
    Typography,
    MenuItem,
    FormControl,
    Select,
    InputLabel, TextField, useMediaQuery
} from '@mui/material';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import MaterialTable from 'material-table';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import {
    currencySymbol,
    price, scrollTop,
    showExactDate,
    showSimpleDate, showTableDate
} from '../../../../config/values';
import {
    isAdmin,
    isUser,
    TableCount,
    TablePage
} from '../../../../config/Permissions';
import { getDataTableLocalization } from '../../../../config/Translate';
import { withStyles } from '@mui/styles';
import { Styles } from '../../../../config/design/styles';
import { tableIcons } from '../../../../config/design/TableIcons';
import { defaultVariables } from '../../../../config';
import { useIsSmall } from '../../../../config/hooks/useWindowDimensions';
import { useTranslation } from 'react-i18next';
import ChevronForward from '../../../components/icons/ChevronForward';
import Title from "../../../components/elements/Title";
import RayaField from "../../../components/elements/fields/RayaField";
import {BellRing} from "lucide-react";
import {theme} from "../../../../config/design/theme";

const Installments = ({ classes }) => {
    const isSmall = useIsSmall();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const statusFilter = searchParams.get('status') || null;
    const tableRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const isMobileOrTablet = useMediaQuery((theme) => theme.breakpoints.down('sm'));

    const [columns] = useState([
        {
            title: t('user'),
            field: 'user',
            filterPlaceholder: t('search'),
            sorting: false,
            hidden: !!isUser(),
            render: rowData => (
                <Typography>
                    {rowData.user.first_name} {rowData.user.last_name}
                </Typography>
            )
        },
        {
            title: t('amount'),
            field: 'user',
            filterPlaceholder: t('amount'),
            sorting: false,
            render: rowData => (
                <Typography style={{whiteSpace:'nowrap'}}>
                    {price(rowData.price)} {currencySymbol}
                </Typography>
            )
        },
        {
            title: t('bsNumber'),
            field: 'type',
            render: rowData => (
                <Typography>
                    {rowData.bs_number}
                </Typography>
            )
        },
        {
            title: t('status'),
            field: 'status',
            lookup: {
                confirmed: <Typography className="table-badge active">{t('confirmed')}</Typography>,
                pending: <Typography className="table-badge pending">{t('pending')}</Typography>,
                not_paid: <Typography className="table-badge not_paid">{t('not_paid')}</Typography>,
                rejected: <Typography className="table-badge inactive">{t('rejected')}</Typography>,
            },
        },
        {
            title: t('due_date'),
            editable: 'never',
            field: 'due_date',
            render: rowData => (
                <Typography className={classes.dateTable}>
                    {showSimpleDate(rowData.due_date)}
                </Typography>
            )
        },
        {
            title: t('confirmation_date'),
            editable: 'never',
            field: 'confirmation_date',
            render: rowData => (
                <Typography className={classes.dateTable}>
                    {showTableDate(rowData.confirmation_date)}
                </Typography>
            )
        },
        {
            title: t('activity'),
            editable: 'never',
            field: 'activity',
            sorting: false,
            render: rowData => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        component={Link}
                        to={`/installments/view/${rowData.id}`}
                        endIcon={<ChevronForward />}
                        size="small"
                        style={{ margin: 5 }}
                        disabled={(!rowData.is_nearest && rowData.status === "not_paid") && !isAdmin()}
                    >
                        {t('details')}
                    </Button>
                    {rowData.is_nearest && (
                        <div className="d-flex flex-column flex-justify-center align-center" style={{animation: 'pulse 1.5s infinite',}}>
                            <BellRing color="#0016ff" strokeWidth={2} size={20}/>
                            <Typography
                                style={{
                                    color: "#0016ff",
                                    fontWeight: 'bold',

                                }}
                                className="ringing-text"
                            >
                                {t('nearest')}
                            </Typography>
                        </div>
                    )}
                </div>
            )
        }
    ]);

    const loadData = () => {
        tableRef.current && tableRef.current.onQueryChange();
    };

    useEffect(() => {
        loadData();
        scrollTop();
    }, [statusFilter]);

    return (
        <>
           <div className="flex-space">
               <Title align="left" color="text" className="panel-title mb-0">{t('installmentsList')}</Title>
           </div>
            {
                !!isMobileOrTablet &&
                    <div style={{paddingTop:5,marginBottom:10, display:'flex', justifyContent:"flex-end"}} >
                        <RayaField
                            value={statusFilter}
                            onChange={(e) => {
                                setSearchParams({ status: e.target.value });
                            }}
                            size="small"

                            label={t('status')}
                            placeholder={t('status')}
                            style={{minWidth:260}}
                            select
                        >
                            <MenuItem value="all">{t('all')}</MenuItem>
                            <MenuItem value="pending">{t('pending')}</MenuItem>
                            <MenuItem value="not_paid">{t('not_paid')}</MenuItem>
                            <MenuItem value="rejected">{t('rejected')}</MenuItem>
                            <MenuItem value="confirmed">{t('confirmed')}</MenuItem>
                        </RayaField>
                    </div>
            }
            <div className="main-table main-table-top-gray">
                <MaterialTable
                    tableRef={tableRef}
                    icons={tableIcons}
                    title={
                        !isMobileOrTablet ?
                        <div style={{paddingTop:5}}>
                            <RayaField
                                value={statusFilter}
                                onChange={(e) => {
                                    setSearchParams({ status: e.target.value });
                                }}
                                size="small"
                                fullWidth
                                label={t('status')}
                                placeholder={t('status')}
                                style={{minWidth:200}}
                                select
                            >
                                <MenuItem value="all">{t('all')}</MenuItem>
                                <MenuItem value="pending">{t('pending')}</MenuItem>
                                <MenuItem value="not_paid">{t('not_paid')}</MenuItem>
                                <MenuItem value="rejected">{t('rejected')}</MenuItem>
                                <MenuItem value="confirmed">{t('confirmed')}</MenuItem>
                            </RayaField>
                        </div>
                            : null
                    }
                    localization={getDataTableLocalization()}
                    columns={columns}
                    data={query =>
                        new Promise((resolve, reject) => {
                            const page = query.page + 1;
                            const perPage = query.pageSize;
                            const search = query.search;
                            const status = searchParams.get('status');

                            let url = `${defaultVariables.url}/installments/quickView?page=${page}&per_page=${perPage}&search=${search}`;
                            if (status) {
                                url += `&status=${status}`;
                            }

                            axios.get(url, {
                                headers: {
                                    Accept: 'application/json',
                                    Authorization: `Bearer ${localStorage.getItem('ACT')}`
                                }
                            }).then(response => {
                                const data = response.data.data.map(row => ({
                                    id: row.id,
                                    bs_number: row.bs_number,
                                    user: row.user,
                                    status: row.status,
                                    price: row.price,
                                    due_date: row.due_date,
                                    confirmation_date: row.confirmation_date,
                                    is_nearest: row.is_nearest,
                                }));
                                resolve({
                                    data,
                                    page: response.data.current_page - 1,
                                    totalCount: response.data.total
                                });
                            }).catch(error => {
                                console.log(error.response);
                                reject();
                            });
                        })
                    }
                    options={{
                        pageSize: TablePage,
                        pageSizeOptions: TableCount,
                        defaultExpanded: false,
                        draggable: false,
                        rowStyle: rowData => rowData.is_nearest
                            ? {
                                backgroundColor: "rgba(0, 37, 255 ,.03)",
                                fontWeight: "bold",
                                color: "rgb(6, 116, 255)",
                                borderRight: "6px solid #0016ff",

                            }
                            : {}
                    }}
                />
            </div>

            <style>
                {`
                    @keyframes pulse {
                        0% { opacity: 1; transform: scale(.9); }
                        50% { opacity: 1; transform: scale(1.05); }
                        100% { opacity: 1; transform: scale(.9); }
                    }
                `}
            </style>
        </>
    );
};

export default withStyles(Styles)(Installments);
